/**
 * Starting something to send: a quote for a client or lead, or a bid on a
 * public solicitation.
 *
 * The two share a document and an editor but not a record — a quote is a
 * proposal, a bid is a bid — so the chooser asks which first, then who it is
 * for, then what to start from, and hands back the row to insert and where to
 * go once it exists. Nothing here touches the database.
 */

import { proposalDraftFromLead } from './leadPipeline'
import { mergeTemplate, autofill } from './documentTemplates'
import { SPINE, emptySection } from './documentSections'
import { proposalFromPreset } from './proposalPresets'
import { generationContext, generateAll } from './proposalGenerate'

const text = (v) => String(v ?? '').trim()

export const KINDS = [
  { id: 'quote', label: 'Quote', blurb: 'For a client or lead. Pick services from the price list. They get a live page to approve, sign and pay.' },
  { id: 'bid', label: 'RFP bid', blurb: 'For a public solicitation. Requirements checklist, suggested services and 3-year pricing.' },
]

// A package is first because it is how most quotes begin: services named,
// priced from the list. Bids are priced requirement by requirement in their
// workspace, so they do not start from one.
export const STARTS = [
  { id: 'package', label: 'A package', quoteOnly: true },
  { id: 'template', label: 'Template' },
  { id: 'past', label: 'A past proposal' },
  { id: 'blank', label: 'Blank' },
]

/** "client:<id>" / "lead:<id>" — one select can offer both. */
export const recipientKey = (type, id) => `${type}:${id}`
export function parseRecipient(key) {
  const [type, ...rest] = String(key || '').split(':')
  const id = rest.join(':')
  return (type === 'client' || type === 'lead') && id ? { type, id } : null
}

/** Fresh section ids, so a copied document never shares an id with its source. */
const freshIds = (sections = []) => (sections || []).map(s => ({
  ...JSON.parse(JSON.stringify(s)),
  id: `${s.type || 'section'}-${Math.random().toString(36).slice(2, 9)}`,
}))

/** The opening of a document started from a template: the template, filled. */
function sectionsFromTemplate(kind, template, values) {
  const merged = mergeTemplate(template, values)
  return (SPINE[kind] || SPINE.proposal).map(type => {
    const s = emptySection(type)
    if (type === 'overview') s.data = { body: merged.text }
    return s
  })
}

/**
 * The proposal row a quote starts as.
 *
 * For a lead, the lead's own draft — attached to the lead, not to a client
 * that does not exist yet. From a past proposal, its sections are copied with
 * new ids and its prices kept as quoted then; the studio shows the list
 * version on each line, so an old price is visible as old.
 */
export function quoteDraft({ recipient = null, record = null, start = 'blank', template = null, past = null, preset = null, catalog = null, settings = null, proposalNumber, token, now = new Date() } = {}) {
  const base = recipient?.type === 'lead'
    ? proposalDraftFromLead(record, { proposalNumber, token })
    : {
      proposal_type: 'rich',
      token,
      proposal_number: proposalNumber,
      lead_id: null,
      client_id: recipient?.type === 'client' ? recipient.id : null,
      title: record ? `Proposal — ${record.company || record.name}` : 'New Proposal',
      status: 'draft',
      content: { sections: [] },
      total: 0,
      validity_days: 30,
    }

  // A past proposal is copied as it was written: nothing is regenerated, so
  // words chosen for one client are visible as such and edited by hand.
  if (start === 'past' && past) {
    const sections = freshIds(past.content?.sections || [])
    const who = record?.company || record?.name
    return {
      ...base,
      title: who && text(past.title) ? `${text(past.title).split(' — ')[0]} — ${who}` : (text(past.title) || base.title),
      content: { sections },
      total: Number(past.total) || 0,
      validity_days: Number(past.validity_days) || base.validity_days,
    }
  }

  // The rest of the document is written from data (lib/proposalGenerate):
  // the letter, the challenge, the approach, the glance, case studies, the
  // team and the terms. Prices are never part of it.
  const generate = (row, sections, { force = false, packageName = '' } = {}) => {
    const ctx = generationContext({ record, catalog: catalog || {}, settings: settings || {}, packageName, validityDays: row.validity_days })
    const doc = generateAll({ kind: 'proposal', sections, team: [], generated: {} }, ctx, { force })
    return {
      ...row,
      content: {
        ...(row.content || {}),
        sections: doc.sections,
        team: doc.team,
        generated: doc.generated,
        ...(packageName ? { package: packageName } : {}),
      },
    }
  }

  // From a package, the document is built here rather than in the studio, so
  // the record is complete — lines, scope, opening, terms, total — the moment
  // it exists, and a studio opened on it later has nothing special to do.
  if (start === 'package' && preset && catalog) {
    const built = proposalFromPreset(preset, catalog, { recipient: text(record?.company || record?.name) })
    // The package's own opening and the raw policy text are replaced by the
    // generated letter and terms: this is a new document, nothing is lost.
    return generate({ ...base, title: built.title, total: built.total || 0 }, built.sections, { force: true, packageName: preset.name })
  }

  if (start === 'template' && template) {
    const client = record ? { ...record, company: record.company || record.name } : null
    const values = autofill({ client, proposal: { validity_days: base.validity_days }, now })
    // The template's words are the letter; everything else is generated.
    return generate(base, sectionsFromTemplate('proposal', template, values))
  }

  if (record) return generate(base, (SPINE.proposal).map(emptySection))

  return base
}

const genericToken = () => Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('')

/**
 * The bid row an RFP response starts as — the same shape the bids page
 * creates, priced from the list (`lines`) rather than the four old numbers.
 *
 * From a past bid, the written response and the priced lines come across; the
 * requirements do not, because they belong to the other solicitation.
 */
export function bidDraft({ title = '', organization = '', start = 'blank', template = null, past = null, token = genericToken(), now = new Date() } = {}) {
  const base = {
    title: text(title) || 'New bid',
    organization: text(organization) || null,
    bid_number: null,
    contact_name: null,
    contact_email: null,
    deadline: null,
    intro: null,
    scope_items: [],
    pricing: { lines: [] },
    status: 'draft',
    token,
  }
  if (start === 'past' && past) {
    return {
      ...base,
      intro: past.intro || null,
      scope_items: Array.isArray(past.scope_items) ? JSON.parse(JSON.stringify(past.scope_items)) : [],
      pricing: Array.isArray(past.pricing?.lines) ? JSON.parse(JSON.stringify(past.pricing)) : { lines: [] },
    }
  }
  if (start === 'template' && template) {
    const values = { ...autofill({ now }), organization: text(organization), project_title: text(title) }
    return { ...base, intro: mergeTemplate(template, values).text || null }
  }
  return base
}

/** The start options a kind offers, in the order they are shown. */
export const startsFor = (kind) => STARTS
  .filter(s => !(s.quoteOnly && kind === 'bid'))
  .map(s => (s.id === 'past' && kind === 'bid' ? { ...s, label: 'A past bid' } : s))

/** Where a kind starts by default: a package for a quote, blank for a bid. */
export const defaultStart = (kind) => (kind === 'bid' ? 'blank' : 'package')

/** Where to go once the row exists. */
export const destination = (kind, id) => (kind === 'bid' ? `/bids/${id}` : `/proposals/${id}/studio`)

/** What stops the dialog from going ahead, in the words the button shows. */
export function chooserBlocker({ kind, organization, start, templateId, pastId, presetId } = {}) {
  // A quote can start before anyone is named — the studio's cover asks again.
  if (kind === 'bid' && !text(organization)) return 'Name the agency'
  if (start === 'package' && kind !== 'bid' && !presetId) return 'Pick a package'
  if (start === 'template' && !templateId) return 'Pick a template'
  if (start === 'past' && !pastId) return kind === 'bid' ? 'Pick a past bid' : 'Pick a past proposal'
  return null
}
